import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle, CalendarDays, ClipboardCheck, Gavel, HeartPulse, RefreshCw, Siren, UserX, Users,
} from "lucide-react";

import { DecisionsCard, DeskTiles, ViewerNote, type DeskTile } from "../../components/dashboard/DeskParts";
import { ErrorState, LoadingState } from "../../components/PageState";
import { ClientRegister, StatusDot, type RegisterColumn } from "../../components/register/ClientRegister";
import {
  classRate, inSchool, longDay, percent, registerConfig, registerStatus, shortDay, viewerNote,
  type ClassRegister, type HeadTeacherDesk,
} from "../../components/headteacher/headTeacherLogic";
import { api } from "../../services/api";

/**
 * The head teacher's dashboard: what is waiting for their decision, then how
 * the school is running today. It shows the same school to an administrator
 * or the director who opens it; no money appears here.
 */
export default function HeadmasterDashboard() {
  const query = useQuery({
    queryKey: ["head-teacher-desk"],
    queryFn: async () => (await api.get("/dashboard/headmaster/")).data as HeadTeacherDesk,
    refetchInterval: 60_000,
  });

  if (query.isLoading) return <LoadingState label="Loading the head teacher's dashboard…" />;
  if (query.isError || !query.data) return <ErrorState title="Dashboard unavailable" message="The head teacher's dashboard could not be loaded. Check your connection and try again." retry={() => query.refetch()} />;

  const data = query.data;
  const note = viewerNote(data.viewer_role);
  const attendance = data.attendance;
  const today = data.school_today;
  const classes = attendance.classes.length;

  const tiles: DeskTile[] = [
    {
      label: "Learners in school",
      value: attendance.marked ? `${attendance.in_school.toLocaleString()}` : "—",
      note: attendance.marked ? `${percent(attendance.rate)} of ${attendance.marked.toLocaleString()} marked · ${attendance.absent} absent` : "No register taken yet today",
      link: "/attendance",
      icon: Users,
    },
    {
      label: "Registers taken",
      value: `${attendance.classes_marked} of ${classes}`,
      note: attendance.classes_marked < classes ? `${classes - attendance.classes_marked} ${classes - attendance.classes_marked === 1 ? "class" : "classes"} still to mark` : "Every class is marked",
      link: "/attendance",
      icon: ClipboardCheck,
      alert: attendance.classes_marked < classes,
    },
    { label: "Open emergencies", value: String(today.open_emergencies), note: today.open_emergencies ? "Not yet resolved" : "None open", link: "/emergency", icon: Siren, alert: today.open_emergencies > 0 },
    { label: "Serious discipline cases", value: String(today.serious_incidents), note: "Level 4 and above, still open", link: "/disciplinary?view=open", icon: Gavel, alert: today.serious_incidents > 0 },
    { label: "Attendance alerts", value: String(today.attendance_alerts), note: "Learners often away, not yet followed up", link: "/attendance?tab=alerts", icon: UserX, alert: today.attendance_alerts > 0 },
    { label: "Sick bay today", value: String(today.sick_bay_visits), note: "Visits; details stay with the nurse", link: "/medical", icon: HeartPulse },
    { label: "Parent meetings today", value: String(today.meetings_today), note: "Booked by teachers", link: "/meetings", icon: CalendarDays },
  ];

  const columns: RegisterColumn<ClassRegister>[] = [
    { header: "Class", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
    { header: "Class teacher", cell: (row) => row.class_teacher || <span className="text-amber-700">None assigned</span>, exportValue: (row) => row.class_teacher },
    { header: "Learners", cell: (row) => row.learners, exportValue: (row) => row.learners, align: "right" },
    { header: "In school", cell: (row) => (row.marked ? inSchool(row) : "—"), exportValue: (row) => (row.marked ? inSchool(row) : ""), align: "right" },
    { header: "Absent", cell: (row) => (row.marked ? <span className={row.absent ? "font-semibold text-amber-800" : undefined}>{row.absent}</span> : "—"), exportValue: (row) => (row.marked ? row.absent : ""), align: "right" },
    { header: "Excused", cell: (row) => (row.marked ? row.excused : "—"), exportValue: (row) => (row.marked ? row.excused : ""), align: "right" },
    { header: "Attendance", cell: (row) => percent(classRate(row)), exportValue: (row) => percent(classRate(row)), align: "right" },
    { header: "Register", cell: (row) => <StatusDot {...registerStatus(row)} />, exportValue: (row) => registerStatus(row).label },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Head teacher's dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">{longDay(data.today)} · what is waiting for a decision, and how the school is running today.</p>
        </div>
        <button type="button" onClick={() => query.refetch()} className="btn btn-outline gap-2">
          <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <ViewerNote text={note} />

      <DecisionsCard decisions={data.decisions} />

      <DeskTiles tiles={tiles} />

      <ClientRegister
        title="Registers today"
        description="Each class's day register for today, in school order. Classes still to mark are the ones to chase."
        actions={<Link to="/attendance" className="btn btn-outline h-9 text-sm">Open attendance</Link>}
        rows={attendance.classes}
        config={registerConfig}
        columns={columns}
        rowKey={(row) => row.id}
        noun={["class", "classes"]}
        exportName={{ file: `registers-${data.today}`, title: `Registers, ${longDay(data.today)}` }}
        emptyText="There are no classes with learners in the current year."
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="card">
          <div className="card-titlebar"><h2 className="font-semibold text-gray-900">Recent school days</h2><span className="text-xs text-gray-500">Day registers</span></div>
          {data.attendance_days.length ? (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500"><th className="px-6 py-2 font-medium">Day</th><th className="px-2 py-2 text-right font-medium">In school</th><th className="px-6 py-2 text-right font-medium">Rate</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {[...data.attendance_days].reverse().map((day) => (
                  <tr key={day.date}>
                    <td className="px-6 py-2 text-gray-700">{shortDay(day.date)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-gray-700">{day.in_school} of {day.marked}</td>
                    <td className="px-6 py-2 text-right">
                      <span className="inline-flex items-center gap-2">
                        <span className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100"><span className="block h-full rounded-full bg-primary-500" style={{ width: `${day.rate ?? 0}%` }} /></span>
                        <span className="w-12 text-right tabular-nums text-gray-900">{percent(day.rate)}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="px-6 py-6 text-center text-sm text-gray-500">No day registers in the last few weeks.</p>}
        </section>

        <section className="card">
          <div className="card-titlebar"><h2 className="font-semibold text-gray-900">Staff away today</h2><Link to="/staff" className="text-xs font-medium text-primary-700 hover:underline">Staff</Link></div>
          {today.staff_on_leave === null ? (
            <p className="px-6 py-6 text-center text-sm text-gray-500">Staff leave is not part of your access.</p>
          ) : today.staff_on_leave.length ? (
            <ul className="divide-y divide-gray-100">
              {today.staff_on_leave.map((person) => (
                <li key={`${person.name}-${person.until}`} className="flex justify-between gap-3 px-6 py-2.5 text-sm">
                  <span><span className="block font-medium text-gray-900">{person.name}</span><span className="text-xs text-gray-500">{person.leave_type}</span></span>
                  <span className="whitespace-nowrap text-xs text-gray-500">back after {shortDay(person.until)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="px-6 py-6 text-center text-sm text-gray-500">No approved leave today.</p>}
        </section>

        <section className="card">
          <div className="card-titlebar"><h2 className="font-semibold text-gray-900">This week</h2><Link to="/events" className="text-xs font-medium text-primary-700 hover:underline">Events</Link></div>
          {today.events.length ? (
            <ul className="divide-y divide-gray-100">
              {today.events.map((event) => (
                <li key={event.id} className="px-6 py-2.5 text-sm">
                  <span className="block font-medium text-gray-900">{event.name}</span>
                  <span className="text-xs text-gray-500">
                    {shortDay(event.start_date)}{event.end_date !== event.start_date ? ` – ${shortDay(event.end_date)}` : ""}
                    {event.start_time ? ` · ${event.start_time.slice(0, 5)}` : ""}{event.venue ? ` · ${event.venue}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          ) : <p className="px-6 py-6 text-center text-sm text-gray-500">No published events in the next seven days.</p>}
        </section>
      </div>

      {(today.open_emergencies > 0 || today.serious_incidents > 0) && (
        <p className="flex items-center gap-2 text-xs text-gray-500"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Emergencies and serious cases are counted school-wide; open them for details.</p>
      )}
      <p className="text-right text-xs text-gray-400">Updated {new Date(data.generated_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</p>
    </div>
  );
}
